// src/components/reporting/RunReportDialog.js - Multi-step report creation dialog
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useAuth } from '../../contexts/AuthContext';
import { useRoles } from '../../hooks/useRoles';
import { useApiCall } from '../../hooks/useApiCall';
import apiService from '../../services/apiService';
import '../../styles/globals.css';
import './Analytics.css';

const RunReportDialog = ({ tenant, onClose, onReportCreated }) => {
  const { user } = useAuth();
  const { userProfile } = useRoles();
  const [step, setStep] = useState(1);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  
  // Customer Intelligence state
  const [dateRange, setDateRange] = useState(null);
  const [foundConversations, setFoundConversations] = useState([]);
  const [conversationCount, setConversationCount] = useState(0);
  
  // Communication Skills state
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedUserConversations, setSelectedUserConversations] = useState([]);
  
  // Common state
  const [reportTitle, setReportTitle] = useState('');
  const [processing, setProcessing] = useState(false);
  const [pollingReportId, setPollingReportId] = useState(null);
  
  const { loading, error, makeRequest } = useApiCall();

  // Check user role limits
  const isSuperAdmin = userProfile?.roles?.includes('SuperAdmin');
  const conversationLimit = isSuperAdmin ? 100 : 25;

  const getDateRange = (range) => {
    const end = new Date();
    const start = new Date();
    
    switch(range) {
      case '7days':
        start.setDate(start.getDate() - 7);
        break;
      case '30days':
        start.setDate(start.getDate() - 30);
        break;
      case '90days':
        start.setDate(start.getDate() - 90);
        break;
      default:
        return null;
    }
    
    return { 
      start: start.toISOString(), 
      end: end.toISOString() 
    };
  };

// Fetch sales conversations for Customer Intelligence
const fetchSalesConversations = async (range) => {
  const dates = getDateRange(range);
  if (!dates) return;

  try {
    // Use tenant ID from prop if available, otherwise fall back to userProfile
    const tenantId = tenant?.id || userProfile?.tenantId;
    
    if (!tenantId) {
      console.error('No tenant ID available');
      return;
    }
    
    const params = new URLSearchParams({
      limit: '100',
      sortBy: 'created_at',
      sortOrder: 'DESC',
      type: 'sales'
    });
    
    const data = await makeRequest(
      () => apiService.get(`/api/v1/tenants/${tenantId}/conversations?${params}`, user),
      'Finding sales conversations...'
    );
    
    if (data.success && data.data) {
      // Filter for conversations within date range
      const salesConvos = (data.data.conversations || data.data).filter(convo => {
        const convoDate = new Date(convo.createdAt);
        return convoDate >= new Date(dates.start) && 
               convoDate <= new Date(dates.end);
      });
      
      // Apply role-based limit
      const limitedConvos = salesConvos.slice(0, conversationLimit);
      
      setFoundConversations(limitedConvos);
      setConversationCount(salesConvos.length);
    }
  } catch (err) {
    console.error('Error fetching conversations:', err);
  }
};

// Fetch users for Communication Skills
const fetchUsers = async () => {
  try {
    // Use tenant ID from prop if available, otherwise fall back to userProfile
    const tenantId = tenant?.id || userProfile?.tenantId;
    
    if (!tenantId) {
      console.error('No tenant ID available');
      return;
    }
    
    const data = await makeRequest(
      () => apiService.get(`/api/v1/tenants/${tenantId}/users`, user),
      'Loading users...'
    );
    
    if (data.success && data.data) {
      // Only show active users with at least 2 conversations
      const activeUsers = (data.data.users || data.data).filter(u => 
        u.status === 'active' && (u.conversationCount || 0) >= 2
      );
      setUsers(activeUsers);
    }
  } catch (err) {
    console.error('Error fetching users:', err);
  }
};

// Fetch conversations for selected user
const fetchUserConversations = async (userId) => {
  try {
    const data = await makeRequest(
      () => apiService.get(`/api/v1/reports/useractivity/${userId}`, user),
      'Loading user conversations...'
    );
    
    if (data.success && data.data) {
      const conversations = data.data.conversations || [];
      setSelectedUserConversations(conversations.slice(0, 20));
    }
  } catch (err) {
    console.error('Error fetching user conversations:', err);
  }
};

  // Handle date range selection for Customer Intelligence
  const handleDateRangeSelect = async (range) => {
    setDateRange(range);
    await fetchSalesConversations(range);
  };

  // Handle user selection for Communication Skills
  const handleUserSelect = async (userId) => {
    const selected = users.find(u => u.id === userId);
    setSelectedUser(selected);
    await fetchUserConversations(userId);
  };

  // Generate default title
  const getDefaultTitle = () => {
    const now = new Date();
    const month = now.toLocaleString('en-US', { month: 'short' });
    const year = now.getFullYear();
    
    if (selectedTemplate === 'customer_intelligence') {
      return `Customer Intelligence - ${month} ${year}`;
    } else if (selectedTemplate === 'communication_skills' && selectedUser) {
      return `${selectedUser.displayName || selectedUser.email} - Communication Skills`;
    }
    return '';
  };

  // Poll for report completion
  const pollReport = async (reportId) => {
    const pollInterval = setInterval(async () => {
      try {
        const data = await apiService.get(`/api/v1/analytics/reports/${reportId}`, user);
        
        if (data.success && data.data) {
          if (data.data.status === 'completed') {
            clearInterval(pollInterval);
            setProcessing(false);
            onReportCreated(reportId);
          } else if (data.data.status === 'failed') {
            clearInterval(pollInterval);
            setProcessing(false);
            alert('Report generation failed: ' + (data.data.error?.message || 'Unknown error'));
          }
        }
      } catch (err) {
        console.error('Error polling report:', err);
        clearInterval(pollInterval);
        setProcessing(false);
      }
    }, 3000);

    return () => clearInterval(pollInterval);
  };

  // Submit report
  const handleRunAnalysis = async () => {
    setProcessing(true);
    
    try {
      let conversationIds = [];
      let analysisMode = 'group';
      
      if (selectedTemplate === 'customer_intelligence') {
        conversationIds = foundConversations.slice(0, conversationLimit).map(c => c.id);
        analysisMode = conversationIds.length === 1 ? 'individual' : 'group';
        } else if (selectedTemplate === 'communication_skills') {
          conversationIds = selectedUserConversations.map(c => c.conversationId || c.id);
          analysisMode = conversationIds.length === 1 ? 'individual' : 'group';
        }
      const title = reportTitle || getDefaultTitle();
      
      const data = await makeRequest(
        () => apiService.post(
          `/api/v1/analytics/reports/templates/${selectedTemplate}`,
          {
            title,
            scope: {
              conversationIds,
              analysisMode
            }
          },
          user
        ),
        'Creating report...'
      );
      
      if (data.success && data.data) {
        setPollingReportId(data.data.reportId);
        setStep(3); // Move to processing step
        await pollReport(data.data.reportId);
      }
    } catch (err) {
      console.error('Error creating report:', err);
      setProcessing(false);
    }
  };

  // Load users when Communication Skills template is selected
  useEffect(() => {
    if (selectedTemplate === 'communication_skills' && step === 2) {
      fetchUsers();
    }
  }, [selectedTemplate, step]);

  const canRunAnalysis = () => {
    if (selectedTemplate === 'customer_intelligence') {
      return foundConversations.length > 0;
    } else if (selectedTemplate === 'communication_skills') {
      return selectedUser && selectedUserConversations.length >= 2;
    }
    return false;
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-dialog" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3 className="modal-title">
            {step === 1 && 'Choose Report Template'}
            {step === 2 && selectedTemplate === 'customer_intelligence' && 'Configure Customer Intelligence'}
            {step === 2 && selectedTemplate === 'communication_skills' && 'Choose the Person to Analyze'}
            {step === 3 && 'Processing Report'}
          </h3>
          <button onClick={onClose} className="modal-close-btn">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="modal-body">
          {/* Step 1: Choose Template */}
          {step === 1 && (
            <div className="template-selection">
              <div 
                className={`template-card ${selectedTemplate === 'customer_intelligence' ? 'selected' : ''}`}
                onClick={() => {
                  setSelectedTemplate('customer_intelligence');
                  setStep(2);
                }}
              >
                <div className="template-icon"><svg className="w-12 h-12 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg></div>
                <h4 className="template-title">Customer Intelligence</h4>
                <p className="template-description">
                  Analyzes customer needs, buying signals, and decision-making
                </p>
                <p className="template-subtext">
                  Analyzes all sales conversations in selected timeframe
                </p>
              </div>

              <div 
                className={`template-card ${selectedTemplate === 'communication_skills' ? 'selected' : ''}`}
                onClick={() => {
                  setSelectedTemplate('communication_skills');
                  setStep(2);
                }}
              >
                <div className="template-icon"><svg className="w-12 h-12 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                  </svg></div>
                <h4 className="template-title">Communication Skills</h4>
                <p className="template-description">
                  Evaluates communication effectiveness and influence strategies
                </p>
                <p className="template-subtext">
                  Analyzes user&apos;s 20 most recent conversations
                </p>
              </div>
            </div>
          )}

          {/* Step 2a: Customer Intelligence Configuration */}
          {step === 2 && selectedTemplate === 'customer_intelligence' && (
            <div className="report-config">
              <div className="form-group">
                <label className="form-label">Select Date Range</label>
                <div className="date-range-buttons">
                  <button
                    onClick={() => handleDateRangeSelect('7days')}
                    className={`btn ${dateRange === '7days' ? 'btn-primary' : 'btn-secondary'}`}
                  >
                    Last 7 Days
                  </button>
                  <button
                    onClick={() => handleDateRangeSelect('30days')}
                    className={`btn ${dateRange === '30days' ? 'btn-primary' : 'btn-secondary'}`}
                  >
                    Last 30 Days
                  </button>
                  <button
                    onClick={() => handleDateRangeSelect('90days')}
                    className={`btn ${dateRange === '90days' ? 'btn-primary' : 'btn-secondary'}`}
                  >
                    Last 90 Days
                  </button>
                </div>
              </div>

              {dateRange && (
                <div className="conversation-count-info">
                  {foundConversations.length === 0 && (
                    <div className="info-message warning">
                      No sales conversations found in this date range
                    </div>
                  )}
                  {foundConversations.length > 0 && foundConversations.length <= conversationLimit && (
                    <div className="info-message success">
                      {foundConversations.length} sales conversation{foundConversations.length !== 1 ? 's' : ''} found
                    </div>
                  )}
                  {conversationCount > conversationLimit && !isSuperAdmin && (
                    <div className="info-message info">
                      {conversationCount} sales conversations found<br/>
                      Will run with the {conversationLimit} most recent
                    </div>
                  )}
                  {conversationCount > conversationLimit && isSuperAdmin && (
                    <div className="info-message success">
                      {conversationCount} sales conversations found
                    </div>
                  )}
                </div>
              )}

              <div className="form-group">
                <label className="form-label">Report Title (optional)</label>
                <input
                  type="text"
                  className="form-input"
                  placeholder={getDefaultTitle()}
                  value={reportTitle}
                  onChange={e => setReportTitle(e.target.value)}
                />
              </div>
            </div>
          )}

          {/* Step 2b: Communication Skills Configuration */}
          {step === 2 && selectedTemplate === 'communication_skills' && (
            <div className="report-config">
              <div className="form-group">
                <label className="form-label">Select User (Minimum 2 conversations)</label>
                <div className="user-search">
                  <input
                    type="text"
                    className="form-input"
                    placeholder="Search users..."
                    onChange={e => {
                      const search = e.target.value.toLowerCase();
                      // Simple client-side filter
                      // In production, you might want server-side search
                    }}
                  />
                </div>
                <div className="user-list">
                  {users.map(u => (
                    <label key={u.id} className="user-radio-item">
                      <input
                        type="radio"
                        name="selectedUser"
                        checked={selectedUser?.id === u.id}
                        onChange={() => handleUserSelect(u.id)}
                      />
                      <span className="user-info">
                        {u.displayName || u.email}
                        {u.conversationCount && (
                          <span className="user-conversation-count">
                            ({u.conversationCount} conversations)
                          </span>
                        )}
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              {selectedUser && (
                <div className="conversation-count-info">
                  {selectedUserConversations.length < 2 && (
                    <div className="info-message warning">
                      Selected user has only {selectedUserConversations.length} conversation<br/>
                      (Minimum is 2 for analysis)
                    </div>
                  )}
                  {selectedUserConversations.length >= 2 && (
                    <div className="info-message success">
                      Will analyze user&apos;s {Math.min(selectedUserConversations.length, 20)} most recent conversations
                    </div>
                  )}
                </div>
              )}

              <div className="form-group">
                <label className="form-label">Report Title (optional)</label>
                <input
                  type="text"
                  className="form-input"
                  placeholder={getDefaultTitle()}
                  value={reportTitle}
                  onChange={e => setReportTitle(e.target.value)}
                />
              </div>
            </div>
          )}

          {/* Step 3: Processing */}
          {step === 3 && (
            <div className="processing-state">
              <div className="loading-spinner"></div>
              <p className="processing-text">
                Analyzing {selectedTemplate === 'customer_intelligence' ? foundConversations.length : selectedUserConversations.length} conversations...
              </p>
              <p className="processing-subtext">
                Estimated time: ~25 seconds
              </p>
              <p className="processing-status">Status: Processing</p>
            </div>
          )}
        </div>

        <div className="modal-footer">
          {step === 2 && (
            <>
              <button
                onClick={() => setStep(1)}
                className="btn btn-secondary"
              >
                Back
              </button>
              <button
                onClick={handleRunAnalysis}
                disabled={!canRunAnalysis() || processing}
                className="btn btn-primary"
              >
                Run Analysis
              </button>
            </>
          )}
          
          {step === 3 && processing && (
            <button onClick={onClose} className="btn btn-secondary">
              Close
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

RunReportDialog.propTypes = {
  tenant: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    name: PropTypes.string.isRequired,
    slug: PropTypes.string
  }),
  onClose: PropTypes.func.isRequired,
  onReportCreated: PropTypes.func.isRequired
};

export default RunReportDialog;